/**
 * Reading of the IP addresses and of the networks the user types, shared by the apps groups panel, which
 * turns them into members of an apps group, and by the apps groups renderer, which matches the resources of
 * the flows against them.
 *
 * Only IPv4 is computed on: the addresses of an IPv6 network are never walked, those addresses are merely
 * recognized so that one of them can be grouped as it is.
 */

// Prefix of the uuids of the resources standing for a configured external IP, either the external IP itself
// ('vim.ExternalIpaddress:bureau') or one address inside it when it is a network ('vim.ExternalIpaddress:bureau:13')
export const EXTERNAL_IP_UUID_PREFIX = "vim.ExternalIpaddress:";

// Prefix of the uuids DC Netscope gives to the addresses it saw in the flows without being able to tie them to
// a resource of the topology or to a configured external IP ('vim.UnknownIpaddress:8.8.8.8')
export const UNKNOWN_IP_UUID_PREFIX = "vim.UnknownIpaddress:";

// Prefix of the uuids of the networks typed as a member of an apps group ('vim.Ipnetwork:192.168.10.0/24').
// No resource of the flows ever wears it: such a member stands for every address falling inside the network.
export const IP_NETWORK_UUID_PREFIX = "vim.Ipnetwork:";

/**
 * Reads an IPv4 address as a number, or undefined when the text is not one.
 */
export const ipv4AddressValue = (address: string): number => {
  let bytes = (address ?? "").split(".");

  if (bytes.length != 4) {
    return undefined;
  }

  let value = 0;
  for (let byte of bytes) {
    if (!/^[0-9]{1,3}$/.test(byte) || Number.parseInt(byte, 10) > 255) {
      return undefined;
    }
    value = value * 256 + Number.parseInt(byte, 10);
  }

  return value;
};

/**
 * Applies an offset to an IPv4 address, as the addresses seen inside a configured external network are only
 * identified by their offset in it. Undefined is returned for anything that is not an IPv4 address: IPv6
 * networks cannot be walked that way, and the caller then falls back on another label.
 */
export const offsetIpv4Address = (address: string, offset: number): string => {
  let value = ipv4AddressValue(address);

  if (value == undefined) {
    return undefined;
  }

  value += offset;
  if (value < 0 || value > 4294967295) {
    return undefined;
  }

  return [value >>> 24, (value >>> 16) & 255, (value >>> 8) & 255, value & 255].join(".");
};

/**
 * Whether the text is an IPv6 address. Those addresses are never walked, they are only recognized so that the
 * user can group one of them as it is.
 */
export const isIpv6Address = (address: string): boolean => {
  // Three groups at the least, so that a lone ':' the user is typing is not taken for an address
  return /^[0-9A-Fa-f]{0,4}(:[0-9A-Fa-f]{0,4}){2,7}$/.test(address ?? "");
};

/**
 * An IPv4 network, as it is read from a CIDR notation ('192.168.10.0/24'): the value of its address, and the
 * mask its addresses share.
 */
export class Ipv4Network {
  constructor(public value: number, public mask: number, public prefixLength: number) {
  }

  // Whether an address falls inside the network
  contains = (address: string): boolean => {
    let addressValue = ipv4AddressValue(address);

    if (addressValue == undefined) {
      return false;
    }

    return ((addressValue & this.mask) >>> 0) == ((this.value & this.mask) >>> 0);
  }

  // Offset of an address in the network, counted from the address the network was written with
  offsetOf = (address: string): number => {
    return ipv4AddressValue(address) - this.value;
  }
}

/**
 * Reads a CIDR notation ('192.168.10.0/24') as a network, or undefined when the text is not one. The address
 * is kept as it was written and is not aligned on the network: DC Netscope counts the offset of an address in
 * a configured external network from the address that was configured, and both have to agree.
 */
export const ipv4Network = (cidr: string): Ipv4Network => {
  let parts = (cidr ?? "").split("/");

  if (parts.length != 2) {
    return undefined;
  }

  let value = ipv4AddressValue(parts[0]);
  let prefixLength = Number.parseInt(parts[1], 10);

  if (value == undefined || !/^[0-9]{1,2}$/.test(parts[1]) || prefixLength < 0 || prefixLength > 32) {
    return undefined;
  }

  let mask = prefixLength == 0 ? 0 : ((-1 << (32 - prefixLength)) >>> 0);

  return new Ipv4Network(value, mask, prefixLength);
};

/**
 * Address an uuid stands for, or undefined when it names something else. Only the uuids DC Netscope builds
 * out of an address carry one: an unknown IP holds the address itself, and a network the CIDR it was typed
 * as. The addresses inside a configured external network are named after an offset in it, which says nothing
 * on its own, and are read back by the panel, the only place holding the external IPs configuration.
 */
export const uuidAddress = (uuid: string): string => {
  if (uuid == undefined) {
    return undefined;
  }

  if (uuid.startsWith(UNKNOWN_IP_UUID_PREFIX)) {
    return uuid.substring(UNKNOWN_IP_UUID_PREFIX.length);
  }

  if (uuid.startsWith(IP_NETWORK_UUID_PREFIX)) {
    return uuid.substring(IP_NETWORK_UUID_PREFIX.length);
  }

  return undefined;
};

/**
 * Label an uuid is shown under when nothing names the resource it points to: the address or the network it
 * holds, the name a configured external IP was given, and the uuid itself for anything else, as a resource
 * of the infrastructure is only named by the inventory.
 */
export const resourceUuidLabel = (uuid: string): string => {
  let address = uuidAddress(uuid);

  if (address != undefined) {
    return address;
  }

  if (uuid != undefined && uuid.startsWith(EXTERNAL_IP_UUID_PREFIX)) {
    return uuid.substring(EXTERNAL_IP_UUID_PREFIX.length);
  }

  return uuid;
};

/**
 * Network a member of an apps group stands for, or undefined when it stands for a single resource. Only the
 * networks typed by the user carry their CIDR: an address inside a configured external network is matched on
 * the uuid of that network instead, as the flows name it after it.
 */
export const memberNetwork = (uuid: string): Ipv4Network => {
  if (uuid == undefined || !uuid.startsWith(IP_NETWORK_UUID_PREFIX)) {
    return undefined;
  }

  return ipv4Network(uuid.substring(IP_NETWORK_UUID_PREFIX.length));
};
